import { useRef, useEffect, useState } from 'react';

const kyushuPrefectures = [
  {
    name: '福岡県',
    cities: ['福岡市', '北九州市', '久留米市', '飯塚市', '大牟田市', '春日市'],
    highlight: true,
  },
  {
    name: '佐賀県',
    cities: ['佐賀市', '唐津市', '鳥栖市', '伊万里市'],
    highlight: false,
  },
  {
    name: '長崎県',
    cities: ['長崎市', '佐世保市', '諫早市', '大村市'],
    highlight: false,
  },
  {
    name: '熊本県',
    cities: ['熊本市', '八代市', '天草市', '荒尾市'],
    highlight: false,
  },
  {
    name: '大分県',
    cities: ['大分市', '別府市', '中津市', '日田市'],
    highlight: false,
  },
  {
    name: '宮崎県',
    cities: ['宮崎市', '都城市', '延岡市', '日南市'],
    highlight: false,
  },
  {
    name: '鹿児島県',
    cities: ['鹿児島市', '霧島市', '薩摩川内市', '鹿屋市'],
    highlight: false,
  },
  {
    name: '沖縄県',
    cities: ['那覇市', '沖縄市', '浦添市', '宜野湾市'],
    highlight: false,
  },
];

const otherRegions = [
  {
    area: '中国地方',
    icon: 'ri-map-pin-2-line',
    prefectures: ['山口', '広島', '岡山', '島根', '鳥取'],
  },
  {
    area: '四国地方',
    icon: 'ri-map-pin-2-line',
    prefectures: ['香川', '愛媛', '高知', '徳島'],
  },
  {
    area: '近畿地方',
    icon: 'ri-map-pin-2-line',
    prefectures: ['大阪', '兵庫', '京都', '滋賀', '奈良', '和歌山'],
  },
];

export default function Regions() {
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.1 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="regions" className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Header */}
        <div ref={titleRef} className="text-center mb-14">
          <span className={`text-amber-600 text-sm font-semibold tracking-widest uppercase mb-3 block transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            AREA
          </span>
          <h2
            className={`text-4xl md:text-5xl font-black text-stone-900 mb-4 transition-all duration-700 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ fontFamily: '\'Noto Serif JP\', serif' }}
          >
            出張買取の対応地域
          </h2>
          <p className={`text-stone-500 text-base max-w-md mx-auto leading-relaxed transition-all duration-700 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            福岡を拠点に<strong className="text-stone-700">九州全県</strong>をメインエリアとして出張買取に対応。<br />
            中国・四国・近畿エリアにも出張いたします。
          </p>
        </div>

        {/* Kyushu Main Feature */}
        <div className={`mb-8 transition-all duration-700 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/6514541deb6a9fa4f32b465c9cfda47e.jpg"
              alt="九州・沖縄全域 出張買取対応エリア — 福岡を拠点に8県すべてに対応"
              title="天神屋 対応地域 — 九州・沖縄・中国・四国・近畿エリアへ出張買取"
              className="w-full h-[260px] md:h-[320px] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-stone-900/85 via-stone-900/50 to-stone-900/20" />
            <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-14">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 rounded-full bg-amber-500 text-white text-xs font-bold tracking-wide">
                  メインエリア
                </span>
                <span className="text-amber-300 text-sm font-medium">福岡本拠点</span>
              </div>
              <h3 className="text-white font-black text-3xl md:text-4xl mb-2" style={{ fontFamily: '\'Noto Serif JP\', serif' }}>
                九州・沖縄 全域
              </h3>
              <p className="text-white/70 text-sm max-w-sm">
                8県すべてに対応。最短当日〜翌日の迅速な出張が可能です。
              </p>
              <div className="flex gap-6 mt-5">
                {[
                  { v: '8', u: '県', l: '対応県数' },
                  { v: '当日', u: '対応', l: '最短スピード' },
                  { v: '0円', u: '', l: '出張費用' },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="text-amber-400 font-black text-2xl leading-none">
                      {s.v}<span className="text-sm ml-0.5">{s.u}</span>
                    </div>
                    <div className="text-white/50 text-xs mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Kyushu Prefectures Grid */}
        <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10 transition-all duration-700 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {kyushuPrefectures.map((pref, i) => (
            <PrefCard key={pref.name} pref={pref} index={i} />
          ))}
        </div>

        {/* Divider */}
        <div className={`flex items-center gap-4 mb-8 transition-all duration-700 delay-400 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex-1 h-px bg-stone-200" />
          <span className="text-stone-400 text-sm font-medium whitespace-nowrap px-2">その他の対応エリア</span>
          <div className="flex-1 h-px bg-stone-200" />
        </div>

        {/* Other Regions */}
        <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 transition-all duration-700 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {otherRegions.map((region, i) => (
            <OtherRegionCard key={region.area} region={region} index={i} />
          ))}
        </div>

        {/* Bottom Notice */}
        <div className={`p-5 rounded-xl bg-amber-50 border border-amber-200 flex flex-col sm:flex-row items-start sm:items-center gap-3 transition-all duration-700 delay-600 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-500 text-white text-xl shrink-0">
            <i className="ri-information-line" />
          </div>
          <div>
            <div className="text-stone-800 font-semibold text-sm">上記エリア以外もご相談ください</div>
            <div className="text-stone-500 text-sm mt-0.5">記載のないエリアへの出張も可能な場合があります。まずはお気軽にお問い合わせください。</div>
          </div>
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="sm:ml-auto px-5 py-2.5 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap shrink-0"
          >
            お問い合わせ
          </a>
        </div>

      </div>
    </section>
  );
}

function PrefCard({ pref, index }: { pref: typeof kyushuPrefectures[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`rounded-xl p-4 border transition-all duration-500 ${
        pref.highlight
          ? 'bg-amber-500 border-amber-400'
          : 'bg-white border-stone-200'
      } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      style={{ transitionDelay: `${index * 50}ms` }}
    >
      <div className="flex items-center gap-2 mb-2.5">
        <div className={`w-5 h-5 flex items-center justify-center ${pref.highlight ? 'text-white' : 'text-amber-500'}`}>
          <i className="ri-map-pin-fill text-sm" />
        </div>
        <span className={`font-bold text-sm ${pref.highlight ? 'text-white' : 'text-stone-800'}`}>
          {pref.name}
        </span>
        {pref.highlight && (
          <span className="ml-auto text-xs bg-white/25 text-white px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
            本拠点
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1">
        {pref.cities.map((city) => (
          <span
            key={city}
            className={`text-xs px-2 py-0.5 rounded-full ${
              pref.highlight
                ? 'bg-white/20 text-white'
                : 'bg-stone-100 text-stone-500'
            }`}
          >
            {city}
          </span>
        ))}
      </div>
    </div>
  );
}

function OtherRegionCard({ region, index }: { region: typeof otherRegions[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`bg-white border border-stone-200 rounded-xl p-5 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-stone-100 text-stone-500 text-sm">
          <i className={region.icon} />
        </div>
        <h4 className="font-bold text-stone-700 text-sm">{region.area}</h4>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {region.prefectures.map((pref) => (
          <span key={pref} className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-stone-500 font-medium">
            {pref}
          </span>
        ))}
      </div>
    </div>
  );
}
